/**
 * Разбор ошибок API: бэк возвращает тело вида
 * { module, code, message, timestamp, retryAfter, debug }.
 *
 * fetchApi пробрасывает исходный AxiosError, поэтому тело доступно
 * в error.response.data, а error.message — это техническое
 * «Request failed with status code 400», которое пользователю показывать нельзя.
 */

export const API_ERROR_CODE = {
  /**
   * Маркировку ОРД у креатива нельзя отключить после включения: креатив уже
   * передан в ОРД, и повторная регистрация создала бы дубли.
   */
  CREATIVE_ORD_MARK_LOCKED: 1455,
  /**
   * Креатив пустой: нет ни текста (content.text), ни вложений
   * (content.mediaItems), ни ссылки для репоста (content.url).
   * Частый случай — вложение не загрузилось и бэк не получил ссылку на файл.
   */
  CREATIVE_CONTENT_EMPTY: 1456,
  /**
   * Публикацию уже взяли в работу — паблишер пытается принять заказ,
   * который к этому моменту забрал другой канал или другая вкладка.
   */
  PUBLICATION_ALREADY_TAKEN: 1595,
  /** Заказ уже запущен — свою цену публикации менять нельзя. */
  PUBLICATION_PRICE_ORDER_LAUNCHED: 1594,
  /** Своя цена бывает только в заказах Reports — у этого заказа поля быть не должно. */
  PUBLICATION_PRICE_NOT_REPORTS: 3417,
  /** Заказ Reports уже запущен или в нём нет каналов. */
  REPORTS_ORDER_LAUNCH_UNAVAILABLE: 3416,
  /** Запуск без оплаты: заказ не Reports. */
  REPORTS_ORDER_NOT_REPORTS: 3415,
  /** Запуск без оплаты: у пользователя нет роли Reports. */
  REPORTS_ROLE_REQUIRED: 1221,
  /** Креативу нужен токен ОРД — без него заказ не запускается целиком. */
  CREATIVE_ORD_TOKEN_REQUIRED: 1451,
  /** По размещению отмечены выплаты паблишеру — отменить его можно, только сняв их. */
  PUBLICATION_HAS_PAYOUTS: 3414,
  /** Заказ или размещение уже отменены. */
  ALREADY_CANCELED: 1434,
  /** Выплата паблишеру по заказу Reports уже отменена. */
  PAYOUT_ALREADY_CANCELLED: 3413,
  /** Сумма выплаты больше остатка — сам остаток бэк пишет в тексте ошибки. */
  PAYOUT_EXCEEDS_REMAINING: 3411,
  /** Сумма выплаты должна быть больше нуля. */
  PAYOUT_AMOUNT_NOT_POSITIVE: 3418,
  /** Размещение ещё не принято — выплату по нему отметить нельзя. */
  PAYOUT_PUBLICATION_NOT_ACCEPTED: 3403,
  /**
   * Канал нельзя отвязать: на нём остались незавершённые публикации.
   * Владелец должен сначала довести их до конца.
   */
  PLACE_HAS_UNFINISHED_PUBLICATIONS: 2410,
  /**
   * Автопостинг невозможен: файл в посте больше лимита Telegram на загрузку.
   * Бот тут ни при чём — показываем текст бэка, в нём размер файла и лимит.
   */
  AUTOPOST_FILE_TOO_LARGE: 3116,
  /**
   * Привязку мессенджера запрашивают чаще раза в 30 секунд.
   * Отдаётся на /user/messenger/bot/user-bind-start.
   */
  MESSENGER_BIND_TOO_OFTEN: 1307,
  /**
   * У обеих учёток есть данные, поэтому автоматически объединить их нельзя.
   * Приезжает через check-auth: привязка завершается в колбэке бота.
   */
  MESSENGER_MERGE_NOT_ALLOWED: 1353,
  /**
   * Бэк не знает такого значения messenger — на клиенте это означает,
   * что мы отправили что-то помимо telegram и max.
   */
  MESSENGER_UNKNOWN: 1354,

  // ===== Корзина рекламодателя (/advert/cart) =====
  /** Корзину изменили в другой вкладке или на другом устройстве — версия устарела. */
  CART_VERSION_CONFLICT: 1660,
  /** Превышен лимит: 500 размещений, 100 постов или размер черновика. */
  CART_LIMIT_EXCEEDED: 1666,
  /** Оформление пустой корзины. */
  CART_EMPTY: 1667,
  /** Оформление не прошло проверку: все проблемы в message через «;». */
  CART_CHECKOUT_INVALID: 1668,
  /** Оформление: канал снят с публикации, в архиве или у владельца не в порядке реквизиты. */
  PLACE_INACTIVE: 1446,
  /** Оформление: договор посредника не найден, не того типа или не зарегистрирован в ОРД. */
  INTERMEDIARY_CONTRACT_INVALID: 1593,
  /** Оформление: вложение поста лежит не в нашем хранилище. */
  CREATIVE_MEDIA_FOREIGN_HOST: 1457,

  // ===== Установка и подтверждение почты =====
  /** Почта уже установлена: set-email-request и email-confirm ей недоступны. */
  EMAIL_ALREADY_SET: 1301,
  /** Запроса на подтверждение нет или он истёк — нужен новый код. */
  EMAIL_CONFIRM_EXPIRED: 1302,
  /** Указан адрес, который уже стоит у этого же аккаунта. */
  EMAIL_SAME_AS_CURRENT: 1311,
  /** Менять нечего: подтверждённой почты у аккаунта нет, её можно только установить. */
  EMAIL_NOT_CONFIRMED: 1214,
  /** Код введён неверно. */
  EMAIL_CODE_INVALID: 1344,
  /** Попытки ввода кода исчерпаны. */
  EMAIL_CODE_ATTEMPTS_EXCEEDED: 1345,
  /** Повторный запрос кода чаще раза в минуту. */
  EMAIL_CODE_TOO_OFTEN: 1346,
  /** Код должен состоять из шести цифр. */
  EMAIL_CODE_FORMAT: 1347,
  /**
   * Ручные действия админа над публикацией (wiki: admin-panel/publication).
   * 1486 — не ошибка ввода, а состояние: выплата паблишеру уже ушла на вывод,
   * откатить перевод нечем. Состояние меняется по таймеру на бэке, поэтому
   * фронт обязан показывать понятный текст, а не только прятать кнопки.
   */
  PUBLICATION_PAYOUT_ALREADY_WITHDRAWN: 1486,
  /** Админское действие возможно только из advertiser-timeout */
  PUBLICATION_ADMIN_ACTION_WRONG_STATUS: 1487,
  /** Ссылку можно отклонить только из link-provided */
  PUBLICATION_LINK_REJECT_WRONG_STATUS: 1488,
  /** Цепочку денежных операций уже откатили — повторный откат невозможен */
  PUBLICATION_ALREADY_REVERTED: 1448,
  /** Публикация чужая */
  PUBLICATION_FORBIDDEN: 1433,
  /** Не админ на админском маршруте */
  NOT_ADMIN: 1231,
} as const;

// Тексты для кодов, которые нужно показать пользователю по-русски.
const MESSAGES_BY_CODE: Record<number, string> = {
  [API_ERROR_CODE.CREATIVE_ORD_MARK_LOCKED]:
    "Креатив нельзя обновить из-за ограничений ОРД: маркировку для него уже включили, и отключить её нельзя. Верните маркировку, чтобы сохранить изменения.",
  [API_ERROR_CODE.CREATIVE_CONTENT_EMPTY]:
    "Пост пустой: добавьте текст, вложение или ссылку для репоста. Если вы прикрепляли файл — возможно, он не загрузился, попробуйте прикрепить его заново.",
  [API_ERROR_CODE.PUBLICATION_ALREADY_TAKEN]:
    "Заказ уже взят в работу. Обновите список заказов.",
  [API_ERROR_CODE.PUBLICATION_PRICE_ORDER_LAUNCHED]: "Заказ уже запущен — цену изменить нельзя.",
  [API_ERROR_CODE.PUBLICATION_PRICE_NOT_REPORTS]: "Своя цена доступна только в заказах Reports.",
  [API_ERROR_CODE.REPORTS_ORDER_LAUNCH_UNAVAILABLE]: "Заказ уже запущен или в нём нет каналов.",
  [API_ERROR_CODE.REPORTS_ORDER_NOT_REPORTS]: "Запуск без оплаты доступен только в заказах Reports.",
  [API_ERROR_CODE.REPORTS_ROLE_REQUIRED]: "Запуск без оплаты доступен только с ролью Reports.",
  [API_ERROR_CODE.CREATIVE_ORD_TOKEN_REQUIRED]:
    "Креатив требует маркировки ОРД, а токена пока нет. Заказ не запущен: паблишерам не ушёл ни один канал.",
  [API_ERROR_CODE.PUBLICATION_HAS_PAYOUTS]:
    "По размещению уже отмечены выплаты паблишеру. Сначала отмените выплаты, потом размещение.",
  [API_ERROR_CODE.PAYOUT_ALREADY_CANCELLED]: "Выплата уже отменена.",
  [API_ERROR_CODE.PAYOUT_AMOUNT_NOT_POSITIVE]: "Сумма выплаты должна быть больше нуля.",
  [API_ERROR_CODE.PAYOUT_PUBLICATION_NOT_ACCEPTED]:
    "Размещение ещё не принято — выплату по нему отметить нельзя.",
  [API_ERROR_CODE.MESSENGER_BIND_TOO_OFTEN]:
    "Мы уже отправили ссылку на привязку. Повторить можно через полминуты.",
  // ТЗ: «нужно показать текст про ручное объединение и поддержку»
  [API_ERROR_CODE.MESSENGER_MERGE_NOT_ALLOWED]:
    "Аккаунт уже содержит данные. Для объединения обратитесь в поддержку:",
  [API_ERROR_CODE.MESSENGER_UNKNOWN]: "Этот мессенджер пока не поддерживается.",
  [API_ERROR_CODE.CART_LIMIT_EXCEEDED]:
    "В корзине может быть не больше 500 размещений — уберите лишние, чтобы сохранить её.",
  [API_ERROR_CODE.CART_EMPTY]: "Корзина пуста — добавьте каналы, чтобы оформить заказ.",
  [API_ERROR_CODE.CART_CHECKOUT_INVALID]:
    "Заказ не оформлен: проверьте посты и каналы в корзине.",
  [API_ERROR_CODE.PLACE_INACTIVE]:
    "Один из каналов сейчас не принимает заказы — удалите его из корзины и оформите заказ ещё раз.",
  [API_ERROR_CODE.INTERMEDIARY_CONTRACT_INVALID]:
    "Договор посредника не найден или ещё не зарегистрирован в ОРД — проверьте данные договора.",
  [API_ERROR_CODE.CREATIVE_MEDIA_FOREIGN_HOST]:
    "Вложение поста не загрузилось на наш сервер — удалите его и прикрепите заново.",

  [API_ERROR_CODE.EMAIL_ALREADY_SET]:
    "У аккаунта уже указана почта — её можно только сменить.",
  [API_ERROR_CODE.EMAIL_CONFIRM_EXPIRED]:
    "Срок действия кода истёк. Запросите новый код.",
  [API_ERROR_CODE.EMAIL_SAME_AS_CURRENT]: "Это ваша текущая почта. Укажите другой адрес.",
  [API_ERROR_CODE.EMAIL_NOT_CONFIRMED]:
    "У аккаунта нет подтверждённой почты — её можно только установить.",
  [API_ERROR_CODE.EMAIL_CODE_INVALID]: "Код введён неверно.",
  [API_ERROR_CODE.EMAIL_CODE_ATTEMPTS_EXCEEDED]:
    "Попытки ввода кода исчерпаны. Запросите новый код.",
  [API_ERROR_CODE.EMAIL_CODE_TOO_OFTEN]:
    "Новый код можно запросить не чаще раза в минуту.",
  [API_ERROR_CODE.EMAIL_CODE_FORMAT]: "Код состоит из шести цифр.",
  [API_ERROR_CODE.PUBLICATION_PAYOUT_ALREADY_WITHDRAWN]:
    "Откатить нельзя, слишком поздно: выплата паблишеру уже ушла на вывод.",
  [API_ERROR_CODE.PUBLICATION_ADMIN_ACTION_WRONG_STATUS]:
    "Действие недоступно в текущем статусе публикации.",
  [API_ERROR_CODE.PUBLICATION_LINK_REJECT_WRONG_STATUS]:
    "Действие недоступно в текущем статусе публикации.",
  [API_ERROR_CODE.PUBLICATION_ALREADY_REVERTED]: "Откат уже был выполнен.",
  [API_ERROR_CODE.PUBLICATION_FORBIDDEN]: "Нет доступа к этой публикации.",
  [API_ERROR_CODE.NOT_ADMIN]: "Нет доступа: действие доступно только администратору.",
};

/**
 * Тело ошибки бэка. Достаём из двух форматов:
 *   - axios (baseApi)  → error.response.data
 *   - simpleApi        → кладёт code/data на сам Error при throw
 */
export const getApiErrorData = (error: any): any =>
  error?.response?.data ?? error?.data ?? (typeof error?.code === "number" ? error : null);

export const getApiErrorCode = (error: any): number | null => {
  const code = getApiErrorData(error)?.code;
  return typeof code === "number" ? code : null;
};

export const isApiErrorCode = (error: any, code: number): boolean =>
  getApiErrorCode(error) === code;

/**
 * Объединение учёток вручную. Один и тот же случай приходит и как 1353,
 * и как HTTP 409 — и на set-email-request, и на email-confirm, если данные
 * появились между отправкой кода и подтверждением.
 */
export const MERGE_NOT_ALLOWED_MESSAGE =
  "Обе учётные записи содержат данные — объединить их автоматически нельзя.";

export const isMergeNotAllowed = (error: any) =>
  getApiErrorCode(error) === API_ERROR_CODE.MESSENGER_MERGE_NOT_ALLOWED ||
  error?.statusCode === 409 ||
  error?.response?.status === 409;

/**
 * Человекочитаемый текст ошибки для пользователя.
 * Приоритет: наш текст по коду → сообщение бэка, если оно на русском → fallback.
 * Английские сообщения бэка не показываем.
 */
export const getApiErrorMessage = (error: any, fallback: string): string => {
  const code = getApiErrorCode(error);
  if (code !== null && MESSAGES_BY_CODE[code]) return MESSAGES_BY_CODE[code];

  const message = getApiErrorData(error)?.message;
  if (typeof message === "string" && /[а-яё]/i.test(message)) return message;

  return fallback;
};
